import { useSmartrrVendorSelector } from "@vendor-app/app/_state/typedVendorReduxHooks";
import { useMemo } from "react";
import { ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import { SellingPlanGroupCatalog } from "@smartrr/shared/entities/SellingPlanGroup/SellingPlanGroupCatalog";
import config from "@vendor-app/config";

type ShopifyThemeCustomizationPreviewPaths = "/account" | "/products" | "/";

// Handle of the subscription widget block (shopify-app/extensions/theme-extension-1/blocks/app-block.liquid).
// Deep links the merchant straight into adding it to the product template's main section, next to buy buttons,
// per https://shopify.dev/docs/apps/build/online-store/theme-app-extensions/configuration#deep-linking
const SUBSCRIPTION_APP_BLOCK_HANDLE = "app-block";

const addSubscriptionAppBlockDeepLinkParams = (url: URL) => {
  url.searchParams.set("addAppBlockId", `${config.shopify.clientId}/${SUBSCRIPTION_APP_BLOCK_HANDLE}`);
  url.searchParams.set("target", "mainSection");
};

export const useThemeCustomizationUrl = (
  shopName: string | undefined,
  themeId: string | undefined,
  previewPath?: ShopifyThemeCustomizationPreviewPaths,
  sellingPlanGroups: ISmartrrSellingPlanGroup[] = []
) => {
  const { purchasables, isLoading: isPurchasablesLoading } = useSmartrrVendorSelector(
    state => state.purchasables
  );

  return useMemo(() => {
    if (!shopName || !themeId || isPurchasablesLoading || !purchasables.length) {
      return "";
    }

    const url = new URL(
      `https://admin.shopify.com/store/${encodeURIComponent(shopName)}/themes/${encodeURIComponent(themeId)}/editor`
    );

    if (!previewPath) {
      return url.toString();
    }

    if (previewPath !== "/products") {
      url.searchParams.set("previewPath", previewPath);
      return url.toString();
    }

    // only preview a product that is actually attached to a (visible) selling plan.
    // when there's no eligible product - e.g. no selling
    // plans exist yet - fall back to the generic editor page.
    const handle = sellingPlanGroups.length
      ? SellingPlanGroupCatalog.fromSellingPlanGroupRecords(sellingPlanGroups).findEligibleProductHandle(
          purchasables
        )
      : undefined;

    if (!handle) {
      url.searchParams.set("previewPath", previewPath);
      return url.toString();
    }

    url.searchParams.set("previewPath", `${previewPath}/${handle}`);
    addSubscriptionAppBlockDeepLinkParams(url);
    return url.toString();
  }, [purchasables, themeId, shopName, isPurchasablesLoading, previewPath, sellingPlanGroups]);
};
